import "./Holiday.css";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { Form, Row, Col } from "react-bootstrap";
import ButtonCom from "../../../components/button/button";
import AddIcon from "@mui/icons-material/Add";
import Modal from "react-bootstrap/Modal";
import { ToastContainer } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { useDispatch, useSelector } from "react-redux";
import {
  commonSaveData,
  initCommonState,
} from "../../../redux/slices/commonSlice";
import {
  deleteMyHolidayPath,
  updateMyHolidayPath,
} from "../../../services/config";
import Widgets from "../../../components/Widgets";
import WidgetsIcon from "../../../components/Widgets/icon";
import CustomPagination from "../../../components/CustomPagination";
import { useGlobalToast } from "../../../stopOldToastGlobally.js";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 1 + i);

const MONTH_OPTIONS = [
  { value: "", label: "All Months" },
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const EXTERNAL_HOLIDAY_API =
  "https://internaltestinghrms.sktexus.com/api/getGovernmentHolidayList";

const GET_MY_HOLIDAY_LIST_API =
  "https://internaltestinghrms.sktexus.com/api/admin/getMyHolidayList";

const API_BASE_URL = "https://internaltestinghrms.sktexus.com";

const safeToast = (toastFn, message, defaultMsg) => {
  if (!toastFn) return;
  const safeMessage =
    typeof message === "string" ? message : defaultMsg || "Operation completed";
  toastFn(safeMessage);
};

const escapeForSQL = (str) => {
  if (!str) return "";
  return str.replace(/'/g, "\\'");
};

const directPost = async (path, payload) => {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    "";

  let apiPath = path;
  if (!path.startsWith("http") && !path.startsWith("/api")) {
    apiPath = `/api${path}`;
  }

  const url = path.startsWith("http") ? path : `${API_BASE_URL}${apiPath}`;

  console.log("📡 Request URL:", url);
  console.log("📦 Request Payload:", JSON.stringify(payload, null, 2));

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error("❌ HTTP Error:", res.status, errorText);
      throw new Error(`HTTP ${res.status}: ${errorText}`);
    }

    const data = await res.json();
    console.log("✅ Response:", data);
    return data;
  } catch (error) {
    console.error("❌ Request failed:", error);
    throw error;
  }
};

const directGet = async (url, params = {}) => {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    "";

  const queryString = new URLSearchParams(params).toString();
  const fullUrl = `${url}${queryString ? `?${queryString}` : ''}`;

  console.log("📡 GET Request URL:", fullUrl);

  try {
    const res = await fetch(fullUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error("❌ HTTP Error:", res.status, errorText);
      throw new Error(`HTTP ${res.status}: ${errorText}`);
    }

    const data = await res.json();
    console.log("✅ GET Response:", data);
    return data;
  } catch (error) {
    console.error("❌ GET Request failed:", error);
    throw error;
  }
};

// ─── Toggle Switch Component ─────────────────────────────────────────────────
const ToggleSwitch = ({
  isEnabled,
  isDisabled,
  label,
  color,
  onChange,
  showStatus = true,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "3px",
    }}
  >
    <div
      onClick={() => !isDisabled && onChange(!isEnabled)}
      style={{
        width: "40px",
        height: "22px",
        borderRadius: "11px",
        backgroundColor: isEnabled ? color : isDisabled ? "#1a1d2e" : "#2a2d3e",
        position: "relative",
        cursor: isDisabled ? "not-allowed" : "pointer",
        transition: "background-color 0.25s",
        opacity: isDisabled ? 0.4 : 1,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "3px",
          left: isEnabled ? "21px" : "3px",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          backgroundColor: "#fff",
          transition: "left 0.25s",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}
      />
    </div>
    {showStatus && (
      <span
        style={{
          fontSize: "9px",
          fontWeight: "700",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: isDisabled ? "#3a3d4e" : isEnabled ? color : "#6b7280",
        }}
      >
        {label}
      </span>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Holiday = () => {
  const params = useParams();
  const moduleId = params.id;
  const dispatch = useDispatch();
  const commonState = useSelector((s) => s.common);
  const toast = useGlobalToast();

  const [holidayList, setHolidayList] = useState([]);
  const [mergedList, setMergedList] = useState([]);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatingKeys, setUpdatingKeys] = useState(new Set());

  const [open, setOpen] = useState(false);
  const [validated, setValidated] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showDel, setShowDel] = useState(false);
  const [delId, setDelId] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [isModalSaving, setIsModalSaving] = useState(false);

  const initialFetchDone = useRef(false);

  const emptyHoliday = {
    moduleId,
    holiday_name: "",
    holiday_date: "",
    rh_status: "0",
    leave_status: "0",
  };
  const [holiday, setHoliday] = useState(emptyHoliday);

  const getRowKey = (h) =>
    `${h.holidayDate || ""}_${(h.holidayName || "").replace(/\s+/g, "_")}`;

  // ── Fetch holidays from getMyHolidayList API (pass holidayId as moduleId) ──
  const fetchHolidayList = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch saved holidays using getMyHolidayList with holidayId = moduleId
      const savedData = await directGet(GET_MY_HOLIDAY_LIST_API, { holidayId: moduleId });
      console.log("📊 Saved holidays from getMyHolidayList:", savedData);

      let holidays = [];
      if (savedData && savedData.code === 200 && savedData.status === true && Array.isArray(savedData.data)) {
        holidays = savedData.data.map((h) => ({
          holidayId: h.holidayId ? parseInt(h.holidayId) : undefined,
          holidayName: h.holidayName,
          holidayDate: h.holidayDate,
          holidayType: h.holidayType || "",
          rhStatus: h.rh_status || "0",
          leaveStatus: h.leave_status || "0",
        }));
      }

      setHolidayList(holidays);
    } catch (error) {
      console.error("Fetch error:", error);
      safeToast(toast.error, "Failed to load holidays", "Failed to load holidays");
      setHolidayList([]);
    } finally {
      setLoading(false);
    }
  }, [moduleId, toast]);

  // ── Initial fetch — only once ──
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchHolidayList();
    }
  }, [fetchHolidayList]);

  // ── Apply year and month filter on frontend ──
  const filteredList = useMemo(() => {
    let filtered = [...holidayList];

    if (selectedYear) {
      filtered = filtered.filter((item) => {
        if (!item.holidayDate) return false;
        return item.holidayDate.substring(0, 4) === selectedYear.toString();
      });
    }

    if (selectedMonth) {
      filtered = filtered.filter((item) => {
        if (!item.holidayDate) return false;
        return item.holidayDate.substring(5, 7) === selectedMonth;
      });
    }

    return filtered;
  }, [holidayList, selectedYear, selectedMonth]);

  useEffect(() => {
    setMergedList(filteredList);
  }, [filteredList]);

  // ─── Save holiday status using updateMyHolidayPath ───
  const saveHolidayStatus = useCallback(
    async (row, rhStatus, leaveStatus) => {
      const holidayId = row.holidayId;

      const holidayDataItem = {
        moduleId: moduleId,
        holiday_name: escapeForSQL(row.holidayName),
        holiday_date: row.holidayDate,
        leave_status: leaveStatus,
        rh_status: rhStatus,
      };

      if (holidayId) {
        holidayDataItem.holidayId = parseInt(holidayId);
        console.log("🔄 UPDATE existing holiday with ID:", holidayId);
      } else {
        console.log("✨ CREATE new holiday");
      }

      const payload = { holidayData: [holidayDataItem] };
      console.log("SAVE payload →", JSON.stringify(payload, null, 2));

      try {
        const result = await directPost(updateMyHolidayPath, payload);
        console.log("SAVE response →", result);

        if (result.code === 200 && result.status !== "false") {
          safeToast(toast.success, result.message || "Holiday saved successfully", "Holiday saved");
          return { success: true };
        } else {
          safeToast(toast.error, result.message || "Failed to save holiday", "Failed to save");
          return { success: false };
        }
      } catch (err) {
        console.error("SAVE error:", err);
        safeToast(toast.error, "Failed to save holiday", "Failed to save");
        return { success: false };
      }
    },
    [moduleId, toast]
  );

  // ── RH Toggle Handler ──
  const handleRHToggle = useCallback(
    async (row, newVal) => {
      const rowKey = getRowKey(row);
      if (updatingKeys.has(rowKey)) return;

      const newRhStatus = newVal ? "1" : "0";
      const newLeaveStatus = "0";

      setUpdatingKeys((prev) => new Set(prev).add(rowKey));

      // Optimistic update
      setHolidayList((prev) =>
        prev.map((item) =>
          item.holidayId === row.holidayId
            ? { ...item, rhStatus: newRhStatus, leaveStatus: newLeaveStatus }
            : item
        )
      );

      const { success } = await saveHolidayStatus(row, newRhStatus, newLeaveStatus);

      if (!success) {
        // Revert on failure
        setHolidayList((prev) =>
          prev.map((item) =>
            item.holidayId === row.holidayId
              ? { ...item, rhStatus: row.rhStatus, leaveStatus: row.leaveStatus }
              : item
          )
        );
      }

      setUpdatingKeys((prev) => {
        const s = new Set(prev);
        s.delete(rowKey);
        return s;
      });
    },
    [saveHolidayStatus, updatingKeys]
  );

  // ── Leave Toggle Handler ──
  const handleLeaveToggle = useCallback(
    async (row, newVal) => {
      const rowKey = getRowKey(row);
      if (updatingKeys.has(rowKey)) return;

      const newLeaveStatus = newVal ? "1" : "0";
      const newRhStatus = "0";

      setUpdatingKeys((prev) => new Set(prev).add(rowKey));

      // Optimistic update
      setHolidayList((prev) =>
        prev.map((item) =>
          item.holidayId === row.holidayId
            ? { ...item, leaveStatus: newLeaveStatus, rhStatus: newRhStatus }
            : item
        )
      );

      const { success } = await saveHolidayStatus(row, newRhStatus, newLeaveStatus);

      if (!success) {
        // Revert on failure
        setHolidayList((prev) =>
          prev.map((item) =>
            item.holidayId === row.holidayId
              ? { ...item, leaveStatus: row.leaveStatus, rhStatus: row.rhStatus }
              : item
          )
        );
      }

      setUpdatingKeys((prev) => {
        const s = new Set(prev);
        s.delete(rowKey);
        return s;
      });
    },
    [saveHolidayStatus, updatingKeys]
  );

  // ── SUCCESS / ERROR Handling (for DELETE via Redux) ──
  useEffect(() => {
    if (commonState.commonSuccess) {
      const successMsg =
        typeof commonState.commonMessage === "string"
          ? commonState.commonMessage
          : "Operation successful";
      safeToast(toast.success, successMsg, "Operation successful");
      dispatch(initCommonState());

      if (isModalSaving) {
        setOpen(false);
        setHoliday(emptyHoliday);
        setValidated(false);
        setIsEdit(false);
        setIsModalSaving(false);
      }

      setTimeout(() => fetchHolidayList(), 500);
    }

    if (commonState.commonError) {
      const errorMsg =
        typeof commonState.commonMessage === "string"
          ? commonState.commonMessage
          : "Something went wrong";
      safeToast(toast.error, errorMsg, "Something went wrong");
      dispatch(initCommonState());
      setIsModalSaving(false);
    }
  }, [
    commonState.commonSuccess,
    commonState.commonError,
    commonState.commonMessage,
    toast,
    dispatch,
    fetchHolidayList,
    isModalSaving,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHoliday((p) => ({ ...p, [name]: value }));
  };

  // ── Add Custom Holiday ──
  const handleSubmit = async (e) => {
    const form = e.currentTarget;
    e.preventDefault();
    e.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setIsModalSaving(true);

    const newHoliday = {
      holidayName: holiday.holiday_name,
      holidayDate: holiday.holiday_date,
      holidayId: null,
    };

    const { success } = await saveHolidayStatus(newHoliday, holiday.rh_status, holiday.leave_status);

    if (success) {
      await fetchHolidayList();
      setOpen(false);
      setHoliday(emptyHoliday);
      setValidated(false);
      setIsEdit(false);
    }

    setIsModalSaving(false);
  };

  // ── Edit Holiday Submit ──
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsModalSaving(true);

    const editRow = {
      holidayName: holiday.holiday_name,
      holidayDate: holiday.holiday_date,
      holidayId: holiday.holidayId,
    };

    const { success } = await saveHolidayStatus(editRow, holiday.rh_status, holiday.leave_status);

    if (success) {
      await fetchHolidayList();
      setOpen(false);
      setHoliday(emptyHoliday);
      setValidated(false);
      setIsEdit(false);
    }

    setIsModalSaving(false);
  };

  const handleClickOpen = () => {
    setHoliday(emptyHoliday);
    setValidated(false);
    setIsEdit(false);
    setOpen(true);
  };

  const handleClose = () => {
    setHoliday(emptyHoliday);
    setValidated(false);
    setOpen(false);
    setIsEdit(false);
    setIsModalSaving(false);
  };

  const handleEdit = (row) => {
    setIsEdit(true);
    setHoliday({
      moduleId,
      holidayId: row.data.holidayId,
      holiday_name: row.data.holidayName,
      holiday_date: row.data.holidayDate,
      rh_status: row.data.rhStatus ?? "0",
      leave_status: row.data.leaveStatus ?? "0",
    });
    setOpen(true);
  };

  const handleDelete = (row) => {
    setDelId(row.data.holidayId);
    setShowDel(true);
  };

  // ── DELETE ──────────────────────────────────────────────────────────────────
  const handleDeleteSubmit = async () => {
    setShowDel(false);

    const payload = { holidayId: parseInt(delId) };
    console.log("DELETE payload →", JSON.stringify(payload, null, 2));

    try {
      const result = await directPost(deleteMyHolidayPath, payload);
      console.log("DELETE response →", result);

      if (result.code === 200 && result.status !== "false") {
        safeToast(toast.success, result.message || "Holiday deleted", "Holiday deleted");
        await fetchHolidayList();
      } else {
        safeToast(toast.error, result.message || "Delete failed", "Delete failed");
      }
    } catch (err) {
      console.error("DELETE error:", err);
      safeToast(toast.error, "Delete failed", "Delete failed");
    }
  };

  const preSubmit = () => document.querySelector("#submitForm")?.click();

  const selectedCount = mergedList.filter((item) => {
    const leaveOn = item.leaveStatus === "1" || item.leaveStatus === 1;
    return leaveOn;
  }).length;

  const columnDefs = useMemo(
    () => [
      {
        headerName: "S.No",
        width: 70,
        valueGetter: (p) => p.node.rowIndex + 1,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "600",
        },
        headerClass: "text-center",
        sortable: false,
        filter: false,
        suppressMovable: true,
        pinned: "left",
      },
      {
        headerName: "Month",
        width: 180,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        headerClass: "text-center",
        valueGetter: (p) => {
          if (!p.data.holidayDate) return "-";
          return new Date(
            p.data.holidayDate + "T00:00:00"
          ).toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric",
          });
        },
      },
      {
        headerName: "Date",
        field: "holidayDate",
        width: 120,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "600",
        },
        headerClass: "text-center",
        cellRenderer: (p) => {
          if (!p.value) return "-";
          return new Date(p.value + "T00:00:00").toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        },
      },
      {
        headerName: "Day",
        width: 130,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        headerClass: "text-center",
        valueGetter: (p) => {
          if (!p.data.holidayDate) return "-";
          return new Date(
            p.data.holidayDate + "T00:00:00"
          ).toLocaleDateString("en-IN", { weekday: "long" });
        },
      },
      {
        headerName: "Holiday Name",
        field: "holidayName",
        flex: 2,
        minWidth: 250,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          paddingLeft: "16px",
          fontWeight: "600",
        },
        headerClass: "text-left",
        cellRenderer: (p) => <span>{p.value}</span>,
      },
      {
        headerName: "Restricted Holiday",
        width: 160,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        headerClass: "text-center",
        sortable: false,
        filter: false,
        cellRenderer: (p) => {
          const rhOn = p.data.rhStatus === "1" || p.data.rhStatus === 1;
          const leaveOn = p.data.leaveStatus === "1" || p.data.leaveStatus === 1;
          const isDisabled = !rhOn && leaveOn;
          const rowKey = getRowKey(p.data);
          const isBusy = updatingKeys.has(rowKey);

          return (
            <div style={{ textAlign: "center" }}>
              <ToggleSwitch
                isEnabled={rhOn}
                isDisabled={isDisabled || isBusy}
                label={rhOn ? "Enabled" : "Disabled"}
                color="#f59e0b"
                onChange={(newVal) => handleRHToggle(p.data, newVal)}
                showStatus={true}
              />
            </div>
          );
        },
      },
      {
        headerName: "Leave",
        width: 140,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        headerClass: "text-center",
        sortable: false,
        filter: false,
        cellRenderer: (p) => {
          const rhOn = p.data.rhStatus === "1" || p.data.rhStatus === 1;
          const leaveOn = p.data.leaveStatus === "1" || p.data.leaveStatus === 1;
          const isDisabled = !leaveOn && rhOn;
          const rowKey = getRowKey(p.data);
          const isBusy = updatingKeys.has(rowKey);

          return (
            <div style={{ textAlign: "center" }}>
              <ToggleSwitch
                isEnabled={leaveOn}
                isDisabled={isDisabled || isBusy}
                label={leaveOn ? "Applicable" : "Not Applicable"}
                color="#2ecc71"
                onChange={(newVal) => handleLeaveToggle(p.data, newVal)}
                showStatus={true}
              />
            </div>
          );
        },
      },
    ],
    [handleRHToggle, handleLeaveToggle, updatingKeys]
  );

  const paginationPageSize = 20;
  const paginationPageSizeSelector = [20, 50, 100];

  if (loading && holidayList.length === 0) {
    return (
      <div className="SettingBody">
        <div className="SettingBody-card mt-3">
          <div style={{ textAlign: "center", padding: "50px", color: "#e2e8f0" }}>
            Loading holidays...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="SettingBody">
      <Modal show={showDel} onHide={() => setShowDel(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="color-white">Delete Holiday</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this holiday?</Modal.Body>
        <Modal.Footer>
          <Button
            style={{
              backgroundColor: "gray",
              color: "#fff",
              height: "30px",
              fontSize: "13px",
              marginRight: "6px",
            }}
            onClick={() => setShowDel(false)}
          >
            Cancel
          </Button>
          <Button
            style={{
              backgroundColor: "red",
              color: "#fff",
              height: "30px",
              fontSize: "13px",
            }}
            onClick={handleDeleteSubmit}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        className="toast-custom"
      />

      <div className="SettingBody-card mt-3">
        <div
          className="Branches-body"
          style={{
            borderRadius: "7px",
            width: "100%",
            backgroundColor: "transparent",
          }}
        >
          <div
            className="Branches-table"
            style={{ width: "100%", marginTop: "-20px" }}
          >
            <Widgets
              icon={
                <WidgetsIcon
                  icon={
                    <Link to="/holiday-module" className="icon-color">
                      <ArrowBackIcon />
                    </Link>
                  }
                  title="Holiday List"
                />
              }
              body={
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "12px",
                      padding: "8px 4px 12px",
                      borderBottom: "1px solid #2a2d3e",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Form.Select
                        value={selectedYear}
                        onChange={(e) =>
                          setSelectedYear(Number(e.target.value))
                        }
                        style={{
                          width: "110px",
                          backgroundColor: "#1a1d2e",
                          color: "#e2e8f0",
                          border: "1px solid #2a2d3e",
                          borderRadius: "8px",
                        }}
                      >
                        {YEAR_OPTIONS.map((yr) => (
                          <option key={yr} value={yr}>
                            {yr}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={{
                          width: "140px",
                          backgroundColor: "#1a1d2e",
                          color: "#e2e8f0",
                          border: "1px solid #2a2d3e",
                          borderRadius: "8px",
                        }}
                      >
                        {MONTH_OPTIONS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </Form.Select>

                      <span
                        style={{
                          fontSize: "11px",
                          color: "#2ecc71",
                          background: "rgba(46,204,113,0.08)",
                          border: "1px solid rgba(46,204,113,0.25)",
                          borderRadius: "20px",
                          padding: "3px 10px",
                          fontWeight: "600",
                        }}
                      >
                        {selectedCount} Selected
                      </span>
                      {loading && (
                        <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                          Loading...
                        </span>
                      )}
                    </div>

                    <ButtonCom
                      type="button"
                      class_name="p_btn universal-btn"
                      text="Add Custom Holiday"
                      ext
                      url_or_onclick={handleClickOpen}
                      icon={<AddIcon />}
                    />
                  </div>

                  <div
                    className="ag-theme-quartz custom-grid"
                    style={{
                      width: "100%",
                      "--ag-header-height": "50px",
                      "--ag-row-height": "52px",
                    }}
                  >
                    <AgGridReact
                      rowData={mergedList}
                      columnDefs={columnDefs}
                      domLayout="autoHeight"
                      pagination={true}
                      paginationPageSize={paginationPageSize}
                      paginationPageSizeSelector={paginationPageSizeSelector}
                      onGridReady={(p) => setGridApi(p.api)}
                      headerHeight={50}
                      rowHeight={52}
                      suppressCellFocus={true}
                      defaultColDef={{
                        resizable: true,
                        sortable: true,
                        filter: true,
                      }}
                      onFirstDataRendered={(p) => p.api.sizeColumnsToFit()}
                      getRowStyle={(p) => {
                        if (!p.data?.holidayId) {
                          return {
                            background: "rgba(108,71,255,0.04)",
                            borderLeft: "3px solid rgba(108,71,255,0.3)",
                          };
                        }
                        return null;
                      }}
                    />
                  </div>

                  <div className="d-flex align-items-center justify-content-between custom-pagination px-3">
                    {gridApi && <CustomPagination gridApi={gridApi} />}
                  </div>
                </>
              }
            />
          </div>
        </div>
      </div>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown
      >
        <DialogTitle className="dialog-title">
          <div className="dialog-title-icon">
            <span className="close-icon" onClick={handleClose}>
              X
            </span>
            <span className="title">
              {isEdit ? "Edit Holiday" : "Add Custom Holiday"}
            </span>
          </div>
          <button className="universal-btn" onClick={preSubmit}>
            {isEdit ? "Update" : "Add"}
          </button>
        </DialogTitle>

        <Form
          noValidate
          validated={validated}
          onSubmit={isEdit ? handleEditSubmit : handleSubmit}
        >
          <DialogContent>
            <Row>
              <Col sm="12">
                <Form.Group className="mb-3">
                  <Form.Label className="color-white">
                    Holiday Name{" "}
                    <span style={{ color: "#ff4444" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="holiday_name"
                    value={holiday.holiday_name}
                    placeholder="e.g. Company Foundation Day"
                    onChange={handleChange}
                    className="universal-input"
                    required
                  />
                </Form.Group>
              </Col>
              <Col sm="12">
                <Form.Group className="mb-3">
                  <Form.Label className="color-white">
                    Holiday Date{" "}
                    <span style={{ color: "#ff4444" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="holiday_date"
                    value={holiday.holiday_date}
                    onChange={handleChange}
                    required
                    className="universal-input"
                  />
                </Form.Group>
              </Col>
            </Row>
          </DialogContent>
          <DialogActions>
            <Button
              type="submit"
              id="submitForm"
              style={{ display: "none" }}
            >
              Save
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </div>
  );
};

export default Holiday;